import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Bot,
  Database,
  GitBranch,
  Activity,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Users,
  Code,

  Edit2,
  Calendar,
  RefreshCw,
  Trash2,
  FolderOpen,
  Settings,
  Globe,
  ShoppingCart,
  BookOpen,
  Upload,
  FileText,
  Tag,
  Plug,
} from 'lucide-react';
import { useProjectStore, useAuthStore, useUIStore } from '../../store';
import Logo from '../common/Logo';
import { ASSISTANT_NAME } from '../../constants/branding';
import { useAllWorkflows } from '../../hooks/queries/useAllWorkflows';
import { useAgents } from '../../hooks/queries/useAgents';
import { useModels } from '../../hooks/queries/useModels';
import { useCreateAgent } from '../../hooks/queries/useAgentMutations';
import { useCreateModel, useUpdateModel } from '../../hooks/queries/useModelMutations';
import { useCreateWorkflow } from '../../hooks/queries/useWorkflowMutations';
import { useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../hooks/queries/queryKeys';
import { useParams, useNavigate } from 'react-router-dom';
import AgentFormModal from '../agents/AgentFormModal';
import ModelFormModal from '../models/ModelFormModal';
import ProjectFormModal from './ProjectFormModal';
import WorkflowFormModal from '../workflow/WorkflowFormModal';
import DomainPurchaseWizard from '../GoDaddy/DomainPurchaseWizard';
import DocumentUploadModal from '../knowledge/DocumentUploadModal';
import { CreateWorkflowForm, ProjectDocumentSummary, PublishedVersionSummary } from '../../types';
import { useAssistantContext } from '../../contexts/AssistantContext';
import apiService from '../../services/api';
import VisibilitySelector from './VisibilitySelector';
import VisibilityBadge from './VisibilityBadge';
import Tooltip from '../common/Tooltip';
import PublishVersionDialog from './PublishVersionDialog';
import VersionHistoryPanel from './VersionHistoryPanel';
import ConfirmationDialog from '../common/ConfirmationDialog';

interface OperationStats {
  totalAgents: number;
  activeAgents: number;
  totalModels: number;
  totalWorkflows: number;
  runningWorkflows: number;
  totalDocuments: number;
  lastUpdated: string;
}


const ProjectDashboard: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  // AC-1412 / AC-1377 P5.1: project agents, models, and workflows all flow
  // through React Query. AC-1410 already migrated `allWorkflows`; this
  // ticket migrates `agents` / `models` and routes the dashboard's
  // create/update calls through the RQ mutation hooks. The mutation hooks
  // own invalidation of their respective keys, so the store's legacy
  // dual-write paths are no longer needed.
  const { currentProject, projects, loadProjects, updateProject, setCurrentOperation, deleteProject } = useProjectStore();
  const allWorkflowsQuery = useAllWorkflows();
  const allWorkflows = allWorkflowsQuery.data ?? [];
  const agentsQuery = useAgents(currentProject?.project_id);
  const modelsQuery = useModels(currentProject?.project_id);
  const agents = useMemo(() => agentsQuery.data ?? [], [agentsQuery.data]);
  const models = useMemo(() => modelsQuery.data ?? [], [modelsQuery.data]);
  const queryClient = useQueryClient();
  const invalidateAllWorkflows = useCallback(
    () => queryClient.invalidateQueries({ queryKey: queryKeys.workflows() }),
    [queryClient],
  );
  const refetchAgents = agentsQuery.refetch;
  const refetchModels = modelsQuery.refetch;
  const createAgentMutation = useCreateAgent(currentProject?.project_id ?? '');
  const createModelMutation = useCreateModel(currentProject?.project_id ?? '');
  const updateModelMutation = useUpdateModel(currentProject?.project_id ?? '');
  const createWorkflowMutation = useCreateWorkflow();
  const { user } = useAuthStore();
  const { openAssistant } = useUIStore();
  const { setContext } = useAssistantContext();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stats, setStats] = useState<OperationStats>({
    totalAgents: 0,
    activeAgents: 0,
    totalModels: 0,
    totalWorkflows: 0,
    runningWorkflows: 0,
    totalDocuments: 0,
    lastUpdated: new Date().toISOString(),
  });
  const [recentActivity, setRecentActivity] = useState<any[]>([]);
  const [activityLoading, setActivityLoading] = useState(false);
  const [showAllActivities, setShowAllActivities] = useState(false);
  const [showAgentModal, setShowAgentModal] = useState(false);
  const [showModelModal, setShowModelModal] = useState(false);
  const [showOperationModal, setShowOperationModal] = useState(false);
  const [showWorkflowModal, setShowWorkflowModal] = useState(false);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [showAllWorkflows, setShowAllWorkflows] = useState(false);
  const [showPurchaseWizard, setShowPurchaseWizard] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [operationDocuments, setOperationDocuments] = useState<ProjectDocumentSummary[]>([]);
  const [showDocumentsPanel, setShowDocumentsPanel] = useState(false);
  const [deletingDocument, setDeletingDocument] = useState<string | null>(null);
  const [documentToDelete, setDocumentToDelete] = useState<string | null>(null);
  const [autonomousPurchasingEnabled, setAutonomousPurchasingEnabled] = useState<boolean | null>(null);
  const [hasGoDaddyCredentials, setHasGoDaddyCredentials] = useState(false);
  const [visibilityLoading, setVisibilityLoading] = useState(false);
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [publishedVersions, setPublishedVersions] = useState<PublishedVersionSummary[]>([]);

  const loadPurchaseRulesStatus = useCallback(async () => {
    if (!projectId) return;

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) return;

      // Check if GoDaddy credentials are configured for this project
      try {
        const credStatus = await apiService.getGoDaddyCredentialsStatus(parseInt(projectId));
        setHasGoDaddyCredentials(credStatus.configured === true);
      } catch {
        setHasGoDaddyCredentials(false);
      }

      const response = await fetch(`/api/v1/projects/${projectId}/purchase-rules`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        const rules = await response.json();
        setAutonomousPurchasingEnabled(rules.autonomous_enabled || false);
      }
    } catch (error) {
      console.error('Failed to load purchase rules status:', error);
    }
  }, [projectId]);

  const loadKnowledgeBaseStats = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiService.getOperationDocuments(parseInt(projectId));
      setOperationDocuments(response.documents);
      setStats(prev => ({ ...prev, totalDocuments: response.total_documents }));
    } catch (error) {
      console.error('Failed to load project documents:', error);
    }
  }, [projectId]);

  const loadPublishedVersions = useCallback(async () => {
    if (!projectId) return;
    try {
      const response = await apiService.listProjectVersions(parseInt(projectId));
      setPublishedVersions(response.versions);
    } catch (error) {
      console.error('Failed to load published versions:', error);
    }
  }, [projectId]);

  const confirmDeleteDocument = useCallback(async () => {
    if (!projectId || !documentToDelete) return;
    const sourceFilename = documentToDelete;
    setDeletingDocument(sourceFilename);
    try {
      await apiService.deleteDocument(sourceFilename, parseInt(projectId));
      await loadKnowledgeBaseStats();
    } catch (error) {
      console.error('Failed to delete document:', error);
      setError('Failed to delete document');
    } finally {
      setDeletingDocument(null);
      setDocumentToDelete(null);
    }
  }, [projectId, documentToDelete, loadKnowledgeBaseStats]);

  const loadRecentActivities = async () => {
    if (!projectId) return;

    try {
      setActivityLoading(true);
      const response = await apiService.getRecentActivities({
        limit: 15,
        daysBack: 7,
        projectId: parseInt(projectId)
      });
      setRecentActivity(response.activities);
    } catch (error) {
      console.error('Failed to load recent activities:', error);
      // Fallback to empty array on error
      setRecentActivity([]);
    } finally {
      setActivityLoading(false);
    }
  };

  // Effect to ensure current project is set when navigating directly to project dashboard
  useEffect(() => {
    const ensureCurrentOperation = async () => {
      if (!projectId) return;
      
      // If currentProject is not set or doesn't match the URL, load and set it
      if (!currentProject || String(currentProject.project_id) !== projectId) {
        try {
          // Load projects if not already loaded
          if (projects.length === 0) {
            await loadProjects();
          }

          // Find and set the current project
          const project = useProjectStore.getState().projects.find(p => String(p.project_id) === projectId);
          if (project) {
            setCurrentOperation(project);
          }
        } catch (err) {
          console.error('Error loading project:', err);
        }
      }
    };

    ensureCurrentOperation();
  }, [projectId, currentProject, projects, loadProjects, setCurrentOperation]);

  // Update AssistantContext when project is loaded
  useEffect(() => {
    if (projectId && currentProject) {
      setContext({
        contextType: 'project',
        projectId: parseInt(projectId)
      });
    }
  }, [projectId, currentProject, setContext]);

  useEffect(() => {
    const loadOperationData = async () => {
      if (!projectId) return;

      setLoading(true);
      setError(null);
      try {
        // AC-1412 / AC-1377 P5.1: useAllWorkflows() / useAgents() /
        // useModels() subscribe on mount and fetch automatically; only
        // the non-RQ side-fetches stay here.
        await Promise.all([
          loadRecentActivities(),
          loadPurchaseRulesStatus(),
          loadKnowledgeBaseStats(),
          loadPublishedVersions()
        ]);
      } catch (err) {
        setError('Failed to load project data');
        console.error('Error loading project data:', err);
      } finally {
        setLoading(false);
      }
    };

    loadOperationData();
  }, [projectId, loadKnowledgeBaseStats, loadPublishedVersions]);

  // Separate useEffect to calculate stats when data changes
  useEffect(() => {
    if (!projectId) return;
    
    // Calculate stats based on loaded data
    const operationWorkflows = allWorkflows.filter(orch => String(orch.project_id) === projectId);
    const runningWorkflows = operationWorkflows.filter(orch => orch.status === 'running').length;

    setStats(prevStats => ({
      ...prevStats,
      totalAgents: agents.length,
      activeAgents: agents.length, // For now, consider all agents as active since there's no status property
      totalModels: models.length,
      totalWorkflows: operationWorkflows.length,
      runningWorkflows: runningWorkflows,
      lastUpdated: new Date().toISOString(),
    }));
  }, [projectId, agents, models, allWorkflows]);

  // Add a refresh function that can be called to update data
  const refreshOperationData = useCallback(async () => {
    if (!projectId) return;

    setLoading(true);
    setError(null);
    try {
      // AC-1412 / AC-1377 P5.1: refresh agents / models / workflows by
      // invalidating their RQ caches (or calling refetch directly). The
      // active subscriptions refetch on invalidation; the non-RQ
      // side-fetches still need manual reload.
      invalidateAllWorkflows();
      await Promise.all([
        refetchAgents(),
        refetchModels(),
        loadRecentActivities(),
        loadPurchaseRulesStatus(),
        loadKnowledgeBaseStats(),
        loadPublishedVersions()
      ]);
    } catch (err) {
      setError('Failed to refresh project data');
      console.error('Error refreshing project data:', err);
    } finally {
      setLoading(false);
    }
  }, [projectId, refetchAgents, refetchModels, invalidateAllWorkflows, loadPurchaseRulesStatus, loadKnowledgeBaseStats, loadPublishedVersions]);

  // Listen for assistant actions and refresh data
  useEffect(() => {
    // Create a custom event listener for assistant actions
    const handleAssistantAction = (event: CustomEvent) => {
      const { action, projectId: actionOperationId } = event.detail;
      
      // Only refresh if the action affects the current project
      if (actionOperationId && String(actionOperationId) === projectId) {
        if (action === 'create_agent' || action === 'update_agent' || action === 'delete_agent' ||
            action === 'create_orchestration' || action === 'create_simple_orchestration' ||
            action === 'create_model' || action === 'update_model' || action === 'delete_model') {
          refreshOperationData();
        }
      }
    };

    // Add event listener
    window.addEventListener('assistant-action', handleAssistantAction as EventListener);
    
    // Cleanup
    return () => {
      window.removeEventListener('assistant-action', handleAssistantAction as EventListener);
    };
  }, [projectId, refreshOperationData]);

  // Handlers for quick actions
  // AC-1412 / AC-1377 P5.1: agent / model / workflow CRUD flows through
  // RQ mutation hooks; each mutation hook owns its own invalidation
  // contract, so the manual loadAgents/loadModels reloads are dropped.
  const handleCreateAgent = async (data: any) => {
    if (!currentProject) return;
    setLoading(true);
    setError(null);
    try {
      await createAgentMutation.mutateAsync({ data });
      setShowAgentModal(false);
    } catch (err) {
      setError('Failed to create agent');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateModel = async (data: any, editId?: number | string) => {
    if (!currentProject) return;
    setLoading(true);
    setError(null);
    try {
      if (editId) {
        await updateModelMutation.mutateAsync({ modelId: editId, data });
      } else {
        await createModelMutation.mutateAsync({ data });
      }
      setShowModelModal(false);
    } catch (err) {
      setError(editId ? 'Failed to update model' : 'Failed to create model');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateOperation = async (data: { name: string; description: string }) => {
    if (!currentProject) return;
    setLoading(true);
    setError(null);
    try {
      await updateProject(currentProject.project_id, data);
      setShowOperationModal(false);
    } catch (err) {
      setError('Failed to update project');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateWorkflow = async (data: CreateWorkflowForm) => {
    if (!currentProject) return;
    setLoading(true);
    setError(null);
    try {
      const workflow = await createWorkflowMutation.mutateAsync({
        projectId: currentProject.project_id,
        data,
      });
      setShowWorkflowModal(false);
      navigate(`/projects/${currentProject.project_id}/workflows/${String(workflow.orchestration_id)}`);
    } catch (err) {
      setError('Failed to create workflow');
      console.error('Error creating workflow:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVisibilityChange = async (visibility: string, teamIds?: number[]) => {
    if (!currentProject) return;
    setVisibilityLoading(true);
    try {
      await updateProject(currentProject.project_id.toString(), {
        visibility,
        team_ids: teamIds,
      });
    } catch (err) {
      setError('Failed to update project visibility');
      console.error('Error updating visibility:', err);
    } finally {
      setVisibilityLoading(false);
    }
  };

  const handleDeleteOperation = () => {
    setDeleteConfirmText('');
    setShowDeleteDialog(true);
  };

  const confirmDeleteProject = async () => {
    if (!currentProject) return;

    setDeleteLoading(true);
    try {
      await deleteProject(currentProject.project_id);
      navigate('/projects');
    } catch (err) {
      setError('Failed to delete project.');
      console.error('Error deleting project:', err);
    } finally {
      setDeleteLoading(false);
      setShowDeleteDialog(false);
    }
  };

  // Domain purchase wizard handlers
  const handleOpenPurchaseWizard = () => {
    setShowPurchaseWizard(true);
  };

  const handlePurchaseComplete = useCallback(() => {
    setShowPurchaseWizard(false);
    // Optionally navigate to GoDaddy settings to see purchase history
    navigate(`/projects/${projectId}/godaddy`);
  }, [navigate, projectId]);

  const handlePurchaseCancel = useCallback(() => {
    setShowPurchaseWizard(false);
  }, []);

  if (!currentProject) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        <div className="text-center py-12">
          <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 dark:text-warm-white mb-2">
            Project not found
          </h3>
          <p className="text-gray-500 dark:text-gray-400">
            The requested project could not be found
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto" data-tutorial="project-overview">
      {/* Project Info Card */}
      <div className="bg-white dark:bg-cap-dark rounded-lg shadow mb-6 sm:mb-8" data-tutorial="project-info-card">
        <div className="p-4 sm:p-6">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2 flex-wrap">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-warm-white">{currentProject.name}</h1>
                <VisibilityBadge visibility={currentProject.visibility ?? (currentProject.is_public ? 'public' : 'private')} />
                               <div className="flex items-center gap-2">
                                   <Tooltip content="Refresh project data">
                    <button
                      onClick={refreshOperationData}
                      className="p-2 text-gray-400 hover:text-primary-600 rounded-lg hover:bg-primary-50 dark:hover:bg-blue-900/30"
                      disabled={loading}
                    >
                      <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                    </button>
                  </Tooltip>
                 <Tooltip content="Edit project">
                   <button
                     onClick={() => setShowOperationModal(true)}
                     className="p-2 text-gray-400 hover:text-gray-600 dark:text-gray-400 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
                   >
                     <Edit2 className="w-5 h-5" />
                   </button>
                 </Tooltip>
                 <Tooltip content="Delete project">
                   <button
                     onClick={handleDeleteOperation}
                     className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30"
                   >
                     <Trash2 className="w-5 h-5" />
                   </button>
                 </Tooltip>
               </div>
              </div>
              <div className="relative mb-4">
                <p className={`text-gray-600 dark:text-gray-400 ${!showFullDescription && 'line-clamp-2'}`}>
                  {currentProject.description || 'No description provided'}
                </p>
                {currentProject.description && currentProject.description.length > 150 && (
                  <button
                    onClick={() => setShowFullDescription(!showFullDescription)}
                    className="text-sm text-primary-600 hover:text-primary-700 mt-1"
                  >
                    {showFullDescription ? 'Show less' : 'Show more'}
                  </button>
                )}
              </div>
              
              {/* Project Metadata */}
              <div className="flex items-center gap-6 pt-4 border-t border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Created</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-warm-white">
                      {currentProject.created_at ? new Date(currentProject.created_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <RefreshCw className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Last Updated</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-warm-white">
                      {currentProject.updated_at ? new Date(currentProject.updated_at).toLocaleDateString() : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Team Members</span>
                    <p className="text-sm font-medium text-gray-900 dark:text-warm-white">1 member</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                  <div>
                    <span className="text-sm text-gray-500 dark:text-gray-400">Visibility</span>
                    <div className="mt-0.5">
                      <VisibilitySelector
                        visibility={currentProject.visibility ?? (currentProject.is_public ? 'public' : 'private')}
                        onVisibilityChange={handleVisibilityChange}
                        loading={visibilityLoading}
                        initialTeamIds={currentProject.team_ids}
                        projectName={currentProject.name}
                        operationDescription={currentProject.description}
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
        <button
          onClick={() => navigate(`/projects/${projectId}/workflows`)}
          className="w-full text-left rounded-lg transition-colors group"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 group-hover:border-primary-400 group-hover:shadow-lg border border-transparent transition-all">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-orange-100 dark:bg-orange-900/30">
                <GitBranch className="w-6 h-6 text-orange-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Workflows</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">{stats.totalWorkflows}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">total workflows</p>
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate(`/projects/${projectId}/agents`)}
          className="w-full text-left rounded-lg transition-colors group"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 group-hover:border-primary-400 group-hover:shadow-lg border border-transparent transition-all">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-blue-100 dark:bg-blue-900/30">
                <Bot className="w-6 h-6 text-primary-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Agents</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">{stats.activeAgents}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">of {stats.totalAgents} total</p>
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={() => navigate(`/projects/${projectId}/schemas`)}
          className="w-full text-left rounded-lg transition-colors group"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow p-6 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 group-hover:border-primary-400 group-hover:shadow-lg border border-transparent transition-all">
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-purple-100 dark:bg-purple-900/30">
                <Database className="w-6 h-6 text-purple-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Schemas</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">{stats.totalModels}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">schemas created</p>
              </div>
            </div>
          </div>
        </button>

        <button
          onClick={() => setShowDocumentsPanel(!showDocumentsPanel)}
          className="w-full text-left rounded-lg transition-colors group"
          aria-label={`View Project Documents. ${stats.totalDocuments} documents.`}
        >
          <div className={`bg-white dark:bg-cap-dark rounded-lg shadow p-6 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/20 group-hover:border-primary-400 group-hover:shadow-lg border ${showDocumentsPanel ? 'border-primary-400 bg-primary-50 dark:bg-primary-900/20' : 'border-transparent'} transition-all`}>
            <div className="flex items-center">
              <div className="p-3 rounded-lg bg-teal-100 dark:bg-teal-900/30">
                <BookOpen className="w-6 h-6 text-teal-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Project Documents</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-warm-white">{stats.totalDocuments}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">documents</p>
              </div>
            </div>
          </div>
        </button>
      </div>

      {/* Project Documents Panel */}
      {showDocumentsPanel && (
        <div className="bg-white dark:bg-cap-dark rounded-lg shadow mb-8">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Project Documents</h2>
            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center gap-2 px-3 py-1.5 text-sm bg-teal-600 text-white rounded-lg hover:bg-teal-700"
            >
              <Upload className="w-4 h-4" />
              Upload
            </button>
          </div>
          <div className="p-6">
            {operationDocuments.length === 0 ? (
              <div className="text-center py-8">
                <BookOpen className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">No documents uploaded yet</p>
                <button
                  onClick={() => setShowUploadModal(true)}
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 text-sm font-medium"
                >
                  Upload Your First Document
                </button>
              </div>
            ) : (
              <div className="space-y-2">
                {operationDocuments.map((doc) => (
                  <div
                    key={doc.source_filename}
                    className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900"
                  >
                    <div className="flex items-center space-x-3 min-w-0">
                      <FileText className="w-5 h-5 text-gray-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 dark:text-warm-white truncate">
                          {doc.source_filename}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {doc.chunk_count} chunks | {new Date(doc.uploaded_at).toLocaleDateString()}
                        </p>
                      </div>
                    </div>
                    <Tooltip content={`Delete ${doc.source_filename}`}>
                    <button
                      onClick={() => setDocumentToDelete(doc.source_filename)}
                      disabled={deletingDocument === doc.source_filename}
                      className="p-1.5 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50 dark:hover:bg-red-900/30 disabled:opacity-50"
                    >
                      {deletingDocument === doc.source_filename ? (
                        <RefreshCw className="w-4 h-4 animate-spin" />
                      ) : (
                        <Trash2 className="w-4 h-4" />
                      )}
                    </button>
                    </Tooltip>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Recent Workflows */}
        <div className="bg-white dark:bg-cap-dark rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Recent Workflows</h2>
              <button
                onClick={() => navigate(`/projects/${projectId}/workflows`)}
                className="text-sm text-primary-600 hover:text-primary-800 hover:underline"
              >
                View All
              </button>
            </div>
          </div>
          <div className="p-6">
            {allWorkflows.filter(orch => String(orch.project_id) === projectId).length === 0 ? (
              <div className="text-center py-8">
                <GitBranch className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                <p className="text-gray-500 dark:text-gray-400 mb-4">No workflows yet</p>
                {/* AC-1702: make the copilot the primary creation path from the
                    empty state; manual creation stays available as a secondary. */}
                <div className="flex flex-col sm:flex-row items-center justify-center gap-2">
                  <button
                    onClick={openAssistant}
                    data-testid="assistant-empty-build"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors text-sm font-medium"
                  >
                    <Logo variant="static-transparent" size={20} />
                    Have {ASSISTANT_NAME} build it
                  </button>
                  <button
                    onClick={() => setShowWorkflowModal(true)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors text-sm font-medium"
                  >
                    Create manually
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div className={`space-y-3 ${showAllWorkflows && allWorkflows.filter(orch => String(orch.project_id) === projectId).length > 8 ? 'max-h-96 overflow-y-auto' : ''}`}>
                  {allWorkflows
                    .filter(orch => String(orch.project_id) === projectId)
                    .sort((a, b) => new Date(b.updated_at || b.created_at || '').getTime() - new Date(a.updated_at || a.created_at || '').getTime())
                    .slice(0, showAllWorkflows ? allWorkflows.filter(orch => String(orch.project_id) === projectId).length : 5)
                    .map((workflow) => (
                      <div
                        key={workflow.orchestration_id}
                        onClick={() => navigate(`/projects/${projectId}/workflows/${workflow.orchestration_id}`)}
                        className="flex items-center justify-between p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:border-primary-400 hover:bg-primary-50 dark:hover:bg-primary-900/20 hover:shadow-md cursor-pointer transition-all"
                      >
                        <div className="flex items-center space-x-3">
                          <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900/30 rounded-lg flex items-center justify-center">
                            <GitBranch className="w-5 h-5 text-orange-600 dark:text-orange-400" />
                          </div>
                          <div>
                            <h3 className="font-medium text-gray-900 dark:text-warm-white">{workflow.name}</h3>
                            <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-1">
                              {workflow.description || 'No description'}
                            </p>
                          </div>
                        </div>
                        <div className="text-sm text-gray-400">
                          {(workflow.updated_at || workflow.created_at) ? new Date(workflow.updated_at || workflow.created_at).toLocaleDateString() : 'Unknown'}
                        </div>
                      </div>
                    ))}
                </div>
                
                {/* Show More/Less Button */}
                {allWorkflows.filter(orch => String(orch.project_id) === projectId).length > 5 && (
                  <div className="pt-4 border-t border-gray-100">
                    <button
                      onClick={() => setShowAllWorkflows(!showAllWorkflows)}
                      className="w-full text-sm text-primary-600 hover:text-primary-800 hover:underline py-2"
                    >
                      {showAllWorkflows ? 'Show Less' : `Show ${allWorkflows.filter(orch => String(orch.project_id) === projectId).length - 5} More`}
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white dark:bg-cap-dark rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Quick Actions</h2>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-2 gap-4">
              {/* AC-1702: project-scoped copilot launcher, headlining the grid. */}
              <button
                onClick={openAssistant}
                data-testid="assistant-launcher-project"
                className="col-span-2 flex items-center justify-center p-4 rounded-lg bg-primary-600 text-white hover:bg-primary-700 shadow-sm transition-colors"
              >
                <div className="text-center flex flex-col items-center">
                  <Logo variant="static-transparent" size={28} className="mb-2" />
                  <span className="text-sm font-semibold">Build with {ASSISTANT_NAME}</span>
                </div>
              </button>

              <button
                onClick={() => setShowAgentModal(true)}
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <div className="text-center">
                  <Bot className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Create Agent</span>
                </div>
              </button>

              <button
                onClick={() => setShowModelModal(true)}
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <div className="text-center">
                  <Database className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Create Schema</span>
                </div>
              </button>

              <button
                onClick={() => setShowWorkflowModal(true)}
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <div className="text-center">
                  <GitBranch className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Create Workflow</span>
                </div>
              </button>


              <button
                onClick={() => navigate(`/projects/${projectId}/integrations`)}
                data-testid="integrations-quick-action"
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <div className="text-center">
                  <Plug className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Integrations</span>
                </div>
              </button>

              {hasGoDaddyCredentials && (
                <button
                  onClick={handleOpenPurchaseWizard}
                  data-testid="purchase-domain-quick-action"
                  className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                >
                  <div className="text-center">
                    <ShoppingCart className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Purchase Domain</span>
                  </div>
                </button>
              )}

              {hasGoDaddyCredentials && (
                <button
                  onClick={() => navigate(`/projects/${projectId}/godaddy`)}
                  data-testid="domain-settings-quick-action"
                  className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors relative"
                >
                  <div className="text-center">
                    <Globe className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Domain Settings</span>
                    {autonomousPurchasingEnabled !== null && (
                      <div className={`mt-1 text-xs px-2 py-0.5 rounded-full ${
                        autonomousPurchasingEnabled
                          ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300'
                          : 'bg-gray-100 text-gray-500 dark:bg-gray-700 dark:text-gray-400'
                      }`}>
                        {autonomousPurchasingEnabled ? 'Auto-Purchase ON' : 'Auto-Purchase OFF'}
                      </div>
                    )}
                  </div>
                </button>
              )}

              <button
                onClick={() => setShowUploadModal(true)}
                data-testid="upload-knowledge-quick-action"
                className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
              >
                <div className="text-center">
                  <Upload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Upload Knowledge</span>
                </div>
              </button>

              {(currentProject.visibility === 'public' || currentProject.visibility === 'team' || currentProject.is_public) && (
                <button
                  onClick={() => setShowPublishDialog(true)}
                  data-testid="publish-version-quick-action"
                  className="flex items-center justify-center p-4 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-primary-300 hover:bg-primary-50 dark:hover:bg-primary-900/20 transition-colors"
                >
                  <div className="text-center">
                    <Tag className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                    <span className="text-sm font-medium text-gray-600 dark:text-gray-400">Publish Version</span>
                  </div>
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Version History */}
      {(currentProject.visibility === 'public' || currentProject.visibility === 'team' || currentProject.is_public) && publishedVersions.length > 0 && (
        <div className="mt-8">
          <VersionHistoryPanel
            projectId={parseInt(projectId || '0')}
            versions={publishedVersions}
            onVersionClick={(checkpointId) => navigate(`/shared/projects/${projectId}?version=${checkpointId}`)}
          />
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-8">
        {/* Recent Activity */}
        <div className="bg-white dark:bg-cap-dark rounded-lg shadow">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700 flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Recent Activity</h2>
            <button
              onClick={() => navigate('/activities')}
              className="text-sm text-primary-600 hover:text-primary-800 hover:underline"
            >
              View All
            </button>
          </div>
          <div className="p-6">
            <div className={`space-y-4 ${showAllActivities && recentActivity.length > 8 ? 'max-h-96 overflow-y-auto' : ''}`}>
              {activityLoading ? (
                <div className="text-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">Loading activities...</p>
                </div>
              ) : recentActivity.length === 0 ? (
                <div className="text-center py-8">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500 dark:text-gray-400">No recent activity for this project</p>
                </div>
              ) : (
                recentActivity
                  .slice(0, showAllActivities ? recentActivity.length : 5)
                  .map((activity) => {
                    const getIcon = () => {
                      // First, determine the entity type from activity_type
                      const activityType = activity.activity_type;
                      let entityIcon = null;
                      let iconColor = 'text-primary-600';

                      // Determine icon based on entity type
                      if (activityType.startsWith('project_')) {
                        entityIcon = <FolderOpen className="w-5 h-5" />;
                        iconColor = 'text-primary-600';
                      } else if (activityType.startsWith('agent_')) {
                        entityIcon = <Bot className="w-5 h-5" />;
                        iconColor = 'text-green-600';
                      } else if (activityType.startsWith('model_')) {
                        entityIcon = <Database className="w-5 h-5" />;
                        iconColor = 'text-purple-600';
                      } else if (activityType.startsWith('orchestration_')) {
                        entityIcon = <GitBranch className="w-5 h-5" />;
                        iconColor = 'text-orange-600';
                      } else {
                        // Fallback to generic activity icon
                        entityIcon = <Activity className="w-5 h-5" />;
                        iconColor = 'text-gray-600 dark:text-gray-400';
                      }

                      // Apply status-based color overrides for errors/warnings
                      if (activity.status === 'error') {
                        iconColor = 'text-red-600';
                      } else if (activity.status === 'warning') {
                        iconColor = 'text-yellow-600';
                      }

                      return React.cloneElement(entityIcon, { className: `w-5 h-5 ${iconColor}` });
                    };

                    const formatTime = (timestamp: string) => {
                      const date = new Date(timestamp);
                      const now = new Date();
                      const diffInMinutes = Math.floor((now.getTime() - date.getTime()) / (1000 * 60));

                      if (diffInMinutes < 1) return 'Just now';
                      if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
                      if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
                      return `${Math.floor(diffInMinutes / 1440)} days ago`;
                    };

                    return (
                      <div key={activity.activity_log_id} className="flex items-start space-x-3">
                        <div className="flex-shrink-0 mt-0.5">{getIcon()}</div>
                        <div className="flex-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-warm-white">
                            {activity.title}
                          </p>
                          <div className="flex items-center mt-1 text-xs text-gray-500 dark:text-gray-400">
                            <Clock className="w-3 h-3 mr-1" />
                            {formatTime(activity.created_at)}
                          </div>
                        </div>
                      </div>
                    );
                  })
              )}

              {/* Show More/Less Button */}
              {recentActivity.length > 5 && (
                <div className="pt-4 border-t border-gray-100">
                  <button
                    onClick={() => setShowAllActivities(!showAllActivities)}
                    className="w-full text-sm text-primary-600 hover:text-primary-800 hover:underline py-2"
                  >
                    {showAllActivities ? 'Show Less' : `Show ${recentActivity.length - 5} More`}
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Agent Creation Modal */}
      {showAgentModal && (
        <AgentFormModal
          agent={null}
          models={models}
          availableAgents={agents}
          onSubmit={handleCreateAgent}
          onClose={() => setShowAgentModal(false)}
        />
      )}
      {/* Model Creation Modal */}
      {showModelModal && (
        <ModelFormModal
          open={showModelModal}
          onClose={() => setShowModelModal(false)}
          onSave={handleCreateModel}
          existingModels={models}
          editModel={null}
        />
      )}

      {/* Project Edit Modal */}
      {showOperationModal && (
        <ProjectFormModal
          open={showOperationModal}
          onClose={() => setShowOperationModal(false)}
          onSave={handleUpdateOperation}
          project={currentProject}
        />
      )}

      {/* Workflow Creation Modal */}
      {showWorkflowModal && (
        <WorkflowFormModal
          isOpen={showWorkflowModal}
          onClose={() => setShowWorkflowModal(false)}
          onSubmit={handleCreateWorkflow}
        />
      )}

      {/* Domain Purchase Wizard Modal */}
      {hasGoDaddyCredentials && showPurchaseWizard && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
          role="dialog"
          aria-modal="true"
          data-testid="purchase-wizard-modal"
        >
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <DomainPurchaseWizard
              projectId={projectId ? parseInt(projectId, 10) : 0}
              onComplete={handlePurchaseComplete}
              onCancel={handlePurchaseCancel}
            />
          </div>
        </div>
      )}

      {/* Publish Version Dialog */}
      <PublishVersionDialog
        projectId={parseInt(projectId || '0')}
        isOpen={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        onPublished={(version) => {
          setPublishedVersions(prev => [version, ...prev]);
        }}
      />

      {/* Document Upload Modal */}
      {showUploadModal && (
        <DocumentUploadModal
          isOpen={showUploadModal}
          onClose={() => setShowUploadModal(false)}
          projectId={projectId ? parseInt(projectId) : undefined}
          onUploadComplete={() => {
            setShowUploadModal(false);
            loadKnowledgeBaseStats();
          }}
        />
      )}

      {/* Loading State */}
      {loading && (
        <div className="fixed inset-0 bg-white dark:bg-night bg-opacity-75 dark:bg-opacity-75 flex items-center justify-center z-50">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="mt-4 text-gray-600 dark:text-gray-400">Loading project data...</p>
          </div>
        </div>
      )}

      {/* Error State */}
      {error && (
        <div className="fixed top-4 right-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-center shadow-lg z-50">
          <AlertTriangle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700 dark:text-red-300">{error}</span>
        </div>
      )}

      {documentToDelete && (
        <ConfirmationDialog
          title="Delete document?"
          message={`Delete "${documentToDelete}" and all its chunks? This cannot be undone.`}
          confirmText="Delete"
          confirmVariant="danger"
          isLoading={deletingDocument === documentToDelete}
          onConfirm={confirmDeleteDocument}
          onCancel={() => setDocumentToDelete(null)}
        />
      )}

      {/* Delete Confirmation Dialog */}
      {showDeleteDialog && currentProject && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-cap-dark rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center space-x-3 mb-4">
              <AlertTriangle className="w-6 h-6 text-red-500" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-warm-white">Delete Project</h3>
            </div>
            
            <div className="mb-6">
              <p className="text-gray-700 dark:text-gray-300 mb-4">
                Are you sure you want to delete <strong>"{currentProject.name}"</strong>?
              </p>
              
              <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-4">
                <div className="flex items-start space-x-2">
                  <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="text-sm font-medium text-red-800 dark:text-red-300 mb-1">
                      This action will permanently delete:
                    </p>
                    <ul className="text-sm text-red-700 dark:text-red-400 space-y-1">
                      <li>• All workflows in this project</li>
                      <li>• All agents in this project</li>
                      <li>• All models in this project</li>
                      <li>• All associated data and configurations</li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                This action cannot be undone. Please make sure you have backed up any important data.
              </p>

              <div>
                <label htmlFor="delete-confirm-input-dashboard" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Type <strong>{currentProject.name}</strong> to confirm:
                </label>
                <input
                  id="delete-confirm-input-dashboard"
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={currentProject.name}
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-warm-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent"
                  autoComplete="off"
                />
              </div>
            </div>

            <div className="flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowDeleteDialog(false);
                  setDeleteConfirmText('');
                }}
                disabled={deleteLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:ring-2 focus:ring-primary-500 focus:border-transparent disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteProject}
                disabled={deleteLoading || deleteConfirmText !== currentProject.name}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 border border-transparent rounded-lg hover:bg-red-700 focus:ring-2 focus:ring-red-500 focus:border-transparent disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleteLoading ? 'Deleting...' : 'Delete Project'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ProjectDashboard; 